import {
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  AddCardOutlined,
  ArrowCircleDown,
  ArrowCircleUp,
  CreditCardOutlined,
  ListAlt,
} from "@mui/icons-material";
import { useState } from "react";

interface TransactionHistoryProps {
  transactions: any;
}

const filters = ["All", "Earned", "Spent", "Purchased", "Redeemed"];
const RECORDS_PER_PAGE = 5;

const TransactionHistory = (props: TransactionHistoryProps) => {
  const { transactions } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeFilter, setActiveFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);

  // Filtered transactions according to activeFilter
  const filteredTransactions = transactions.filter(
    (tx: any) => activeFilter === "All" || tx.type === activeFilter
  );

  // Pagination calculations
  const totalPages = Math.ceil(filteredTransactions.length / RECORDS_PER_PAGE);
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * RECORDS_PER_PAGE,
    currentPage * RECORDS_PER_PAGE
  );

  // Handlers
  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };
  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
    setCurrentPage(1); // reset to first page on filter change
  };

  return (
    <Grid item xs={12} md={8}>
      <Typography variant="h6" gutterBottom>
        Transaction History
      </Typography>

      {/* Filter chips */}
      <Stack direction="row" alignItems="center" flexWrap="wrap" mb={2} gap={1}>
        {filters.map((filter) => (
          <Chip
            key={filter}
            icon={
              filter === "All" ? (
                <ListAlt />
              ) : filter === "Earned" ? (
                <AddCardOutlined />
              ) : filter === "Spent" ? (
                <CreditCardOutlined />
              ) : filter === "Purchased" ? (
                <ArrowCircleUp />
              ) : filter === "Redeemed" ? (
                <ArrowCircleDown />
              ) : undefined
            }
            label={filter}
            onClick={() => handleFilterChange(filter)}
            sx={{
              px: 2,
              py: 1,
              borderRadius: 3,
              backgroundColor: activeFilter === filter ? "#EDE9FE" : "#F9FAFB",
              color: activeFilter === filter ? "#4C1D95" : "#111827",
              border:
                activeFilter === filter
                  ? "2px solid #8B5CF6"
                  : "1px solid var(--neutral-200)",
              fontWeight: 500,
              cursor: "pointer",
            }}
          />
        ))}
      </Stack>

      {/* Transaction Table */}
      <Paper variant="outlined">
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 600 }}>
            <TableHead sx={{ backgroundColor: "#f0f0f0" }}>
              <TableRow>
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                    backgroundColor: "#f0f0f0",
                  }}
                >
                  S.N.
                </TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Source</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedTransactions.map((tx: any, idx: number) => (
                <TableRow key={tx.sn}>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                      backgroundColor: "#fff",
                    }}
                  >
                    {tx.sn}
                  </TableCell>
                  <TableCell>{tx.date}</TableCell>
                  <TableCell>{tx.type}</TableCell>
                  <TableCell
                    sx={{
                      color: tx.amount > 0 ? "green" : "red",
                      fontWeight: "medium",
                    }}
                  >
                    {tx.amount > 0 ? `+${tx.amount}` : tx.amount}
                  </TableCell>
                  <TableCell>{tx.source}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      {/* Pagination Controls */}
      <Stack
        direction="row"
        spacing={2}
        justifyContent="flex-end"
        alignItems="center"
        mt={2}
      >
        <Button
          variant="outlined"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          sx={{
            backgroundColor: "#F9FAFB",
            color: "#111827",
            border: "1px solid var(--neutral-200)",
          }}
        >
          Previous
        </Button>
        <Typography>
          Page {currentPage} of {totalPages}
        </Typography>
        <Button
          variant="outlined"
          onClick={handleNextPage}
          disabled={currentPage === totalPages || totalPages === 0}
          sx={{
            backgroundColor: "#F9FAFB",
            color: "#111827",
            border: "1px solid var(--neutral-200)",
          }}
        >
          Next
        </Button>
      </Stack>
    </Grid>
  );
};

export default TransactionHistory;
